import { useEffect, useRef } from "react";
import type { Dispatch, RefObject, SetStateAction } from "react";

type UseInlineVideoPlaybackParams = {
  inlineVideoRef: RefObject<HTMLVideoElement | null>;
  isPaused: boolean;
  setIsPaused: Dispatch<SetStateAction<boolean>>;
  activeSlideIndex: number;
  activeMediaType?: "video" | "image";
  activeMediaUrl?: string;
};

type UseInlineVideoPlaybackResult = {
  handleTogglePause: () => void;
};

export const useInlineVideoPlayback = ({
  inlineVideoRef,
  isPaused,
  setIsPaused,
  activeSlideIndex,
  activeMediaType,
  activeMediaUrl,
}: UseInlineVideoPlaybackParams): UseInlineVideoPlaybackResult => {
  const pausedVideoTimeRef = useRef(0);

  const handleTogglePause = () => {
    setIsPaused((prev) => {
      const nextPaused = !prev;
      const inlineVideo = inlineVideoRef.current;

      if (inlineVideo) {
        if (nextPaused) {
          pausedVideoTimeRef.current = inlineVideo.currentTime || 0;
          inlineVideo.pause();
        } else {
          if (pausedVideoTimeRef.current > 0) {
            try {
              inlineVideo.currentTime = pausedVideoTimeRef.current;
            } catch {
              // no-op if browser blocks seek before metadata
            }
          }

          const playPromise = inlineVideo.play();
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => undefined);
          }
        }
      }

      return nextPaused;
    });
  };

  useEffect(() => {
    const inlineVideo = inlineVideoRef.current;

    if (!inlineVideo) return;

    if (isPaused) {
      inlineVideo.pause();
      return;
    }

    const playPromise = inlineVideo.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        setIsPaused(true);
      });
    }
  }, [activeSlideIndex, inlineVideoRef, isPaused, setIsPaused]);

  useEffect(() => {
    const inlineVideo = inlineVideoRef.current;
    if (!inlineVideo || activeMediaType !== "video") return;
    pausedVideoTimeRef.current = 0;

    const resetVideoToStart = () => {
      try {
        inlineVideo.currentTime = 0;
      } catch {
        // no-op if browser blocks seek before metadata
      }
    };

    if (inlineVideo.readyState > 0) {
      resetVideoToStart();
    } else {
      inlineVideo.addEventListener("loadedmetadata", resetVideoToStart, { once: true });
    }
  }, [activeSlideIndex, activeMediaType, activeMediaUrl, inlineVideoRef]);

  return { handleTogglePause };
};
